import { z } from 'zod';

export enum SkillChangeTargetKind {
  ACTION_DEFINITION = 'ACTION_DEFINITION',
  GLOBAL_PROMPT = 'GLOBAL_PROMPT',
}

export enum SkillChangeApprovalOutcome {
  APPLIED = 'APPLIED',
  DENIED = 'DENIED',
}

export enum SkillChangeTestRunStatus {
  RUNNING = 'RUNNING',
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  NO_TESTS = 'NO_TESTS',
  ERROR = 'ERROR',
}

export const SkillChangeTestRunSchema = z
  .object({
    status: z.nativeEnum(SkillChangeTestRunStatus),
    ranCount: z.number().int().nonnegative().optional(),
    failedCount: z.number().int().nonnegative().optional(),
  })
  .strict();

export type SkillChangeTestRun = z.infer<typeof SkillChangeTestRunSchema>;

export enum SkillChangeResolvedVia {
  INTERNAL = 'INTERNAL',
  SLACK = 'SLACK',
  TEAMS = 'TEAMS',
  WEB = 'WEB',
}

export const SkillChangeParameterToolSchema = z
  .object({
    canonicalToolType: z.string(),
  })
  .passthrough();

export const SkillChangeParameterSchema = z
  .object({
    name: z.string(),
    description: z.string().optional(),
    default: z.string(),
    defaultTools: z.array(SkillChangeParameterToolSchema).optional(),
  })
  .strict();

export type SkillChangeParameter = z.infer<typeof SkillChangeParameterSchema>;

export const SkillChangeGlobalPromptValueChangeSchema = z
  .object({
    name: z.string(),
    baseValue: z.string().optional(),
    defaultValue: z.string(),
    proposedValue: z.string(),
  })
  .strict();

export type SkillChangeGlobalPromptValueChange = z.infer<
  typeof SkillChangeGlobalPromptValueChangeSchema
>;

const skillChangeApprovalRequestSchema = z
  .object({
    targetKind: z.nativeEnum(SkillChangeTargetKind),
    skillId: z.string(),
    skillName: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    baseInstructions: z.string(),
    proposedInstructions: z.string(),
    diff: z.string(),
    rationale: z.string().optional(),
    requestingConversationId: z.string().optional(),
    requestingActionExecutionId: z.string().optional(),
    baseParameters: z.array(SkillChangeParameterSchema).optional(),
    proposedParameters: z.array(SkillChangeParameterSchema).optional(),
    globalPromptValueChanges: z
      .array(SkillChangeGlobalPromptValueChangeSchema)
      .optional(),
  })
  .strict();

type SkillChangeApprovalRequest = z.infer<
  typeof skillChangeApprovalRequestSchema
>;

function requireBaseParametersWithProposed(
  context: SkillChangeApprovalRequest,
  ctx: z.RefinementCtx
): void {
  if (
    context.proposedParameters !== undefined &&
    context.baseParameters === undefined
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['baseParameters'],
      message:
        'baseParameters is required when proposedParameters is present so the apply-time staleness check has a baseline to compare against.',
    });
  }
}

export const SkillChangeApprovalContextSchema =
  skillChangeApprovalRequestSchema.superRefine(
    requireBaseParametersWithProposed
  );

export type SkillChangeApprovalContext = z.infer<
  typeof SkillChangeApprovalContextSchema
>;

const resolutionBase = {
  resolvedByUserId: z.string(),
  resolvedVia: z.nativeEnum(SkillChangeResolvedVia),
  rationale: z.string().optional(),
};

export const SkillChangeApprovalResolutionSchema = z.discriminatedUnion(
  'outcome',
  [
    z
      .object({
        outcome: z.literal(SkillChangeApprovalOutcome.APPLIED),
        ...resolutionBase,
      })
      .strict(),
    z
      .object({
        outcome: z.literal(SkillChangeApprovalOutcome.DENIED),
        ...resolutionBase,
      })
      .strict(),
  ]
);

export type SkillChangeApprovalResolution = z.infer<
  typeof SkillChangeApprovalResolutionSchema
>;

export const SkillChangeExceptionContextSchema =
  skillChangeApprovalRequestSchema
    .extend({
      skillChangeApproval: SkillChangeApprovalResolutionSchema.optional(),
      skillChangeTests: SkillChangeTestRunSchema.optional(),
    })
    .strict()
    .superRefine(requireBaseParametersWithProposed);

export type SkillChangeExceptionContext = z.infer<
  typeof SkillChangeExceptionContextSchema
>;
